// quickCorsRuntimeService.js
// Drop-in replacement for your existing runtimeDeprecationService.js
// Just replace the import and it should work immediately

class QuickCorsRuntimeService {
  constructor() {
    this.cacheKey = 'aws-lambda-runtime-deprecation';
    this.cacheExpiry = 24 * 60 * 60 * 1000; // 24 hours
    this.awsRuntimesUrl = 'https://docs.aws.amazon.com/lambda/latest/dg/lambda-runtimes.html';
    
    // Multiple CORS proxies for reliability
    this.corsProxies = [
      'https://api.allorigins.win/get?url=',
      'https://corsproxy.io/?',
    ];
    this.currentProxyIndex = 0;
  }

  async getRuntimeDeprecationData() {
    try {
      // Check cache first
      const cachedData = this.getCachedData();
      if (cachedData) {
        console.log('✅ Using cached runtime deprecation data');
        return cachedData;
      }

      console.log('🔄 Fetching runtime deprecation data via CORS proxy...');
      
      // Try each proxy until one works
      for (let i = 0; i < this.corsProxies.length; i++) {
        try {
          const proxy = this.corsProxies[this.currentProxyIndex];
          const runtimeData = await this.fetchWithProxy(proxy);
          
          // Cache successful result
          this.setCachedData(runtimeData);
          console.log(`✅ Successfully fetched data via proxy ${this.currentProxyIndex + 1}`);
          
          return runtimeData;
        } catch (error) {
          console.warn(`❌ Proxy ${this.currentProxyIndex + 1} failed:`, error.message);
          this.currentProxyIndex = (this.currentProxyIndex + 1) % this.corsProxies.length;
          
          if (i === this.corsProxies.length - 1) {
            throw error; // All proxies failed
          }
        }
      }
    } catch (error) {
      console.error('❌ All proxies failed, using fallback data:', error);
      
      // Try cached data even if expired
      const fallbackData = this.getCachedData(true);
      if (fallbackData) {
        console.warn('⚠️ Using expired cached data');
        return fallbackData;
      }
      
      // Use hardcoded fallback
      return this.getHardcodedFallbackData();
    }
  }

  async fetchWithProxy(proxyUrl) {
    const targetUrl = encodeURIComponent(this.awsRuntimesUrl);
    const fullUrl = proxyUrl + targetUrl;
    
    const response = await fetch(fullUrl, {
      method: 'GET',
      headers: {
        'Accept': 'application/json, text/html',
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    let html;
    if (proxyUrl.includes('allorigins.win')) {
      const data = await response.json();
      html = data.contents;
    } else {
      html = await response.text();
    }
    
    return this.parseRuntimeTable(html);
  }

  parseRuntimeTable(html) {
    const runtimeMap = new Map();
    
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      const tables = doc.querySelectorAll('table');
      
      for (const table of tables) {
        const rows = table.querySelectorAll('tr');
        if (rows.length === 0) continue;
        
        const headerRow = rows[0];
        const headers = Array.from(headerRow.querySelectorAll('th, td')).map(th => 
          th.textContent.trim().toLowerCase()
        );
        
        // Look for runtime table
        if (this.isRuntimeTable(headers)) {
          for (let i = 1; i < rows.length; i++) {
            const row = rows[i];
            const cells = row.querySelectorAll('td');
            
            if (cells.length >= 6) {
              const runtimeInfo = this.parseRuntimeRow(cells);
              if (runtimeInfo && runtimeInfo.identifier) {
                runtimeMap.set(runtimeInfo.identifier, runtimeInfo);
              }
            }
          }
          
          if (runtimeMap.size > 0) break;
        }
      }
      
      if (runtimeMap.size === 0) {
        throw new Error('No runtime data found');
      }
      
      return runtimeMap;
    } catch (error) {
      throw new Error(`Parse failed: ${error.message}`);
    }
  }

  isRuntimeTable(headers) {
    const required = ['identifier', 'deprecation', 'block function create', 'block function update'];
    return required.filter(r => headers.some(h => h.includes(r))).length >= 3;
  }

  parseRuntimeRow(cells) {
    try {
      const name = this.extractCellText(cells[0]);
      const identifier = this.extractCellText(cells[1]);
      const deprecationDate = this.extractCellText(cells[3]);
      const blockFunctionCreate = this.extractCellText(cells[4]);
      const blockFunctionUpdate = this.extractCellText(cells[5]);

      if (!identifier || !name) return null;

      return {
        name,
        identifier,
        deprecationDate: this.parseDate(deprecationDate),
        blockFunctionCreate: this.parseDate(blockFunctionCreate),
        blockFunctionUpdate: this.parseDate(blockFunctionUpdate),
        rawDeprecationDate: deprecationDate,
        rawBlockFunctionCreate: blockFunctionCreate,
        rawBlockFunctionUpdate: blockFunctionUpdate,
      };
    } catch (error) {
      return null;
    }
  }

  extractCellText(cell) {
    if (!cell) return '';
    const code = cell.querySelector('code');
    return code ? code.textContent.trim() : cell.textContent.trim();
  }

  parseDate(dateStr) {
    if (!dateStr || dateStr.toLowerCase().includes('not scheduled') || dateStr.toLowerCase().includes('n/a')) {
      return null;
    }
    try {
      const date = new Date(dateStr);
      return isNaN(date.getTime()) ? null : date;
    } catch {
      return null;
    }
  }

  async getRuntimeStatus(runtimeIdentifier) {
    const runtimeData = await this.getRuntimeDeprecationData();
    const runtimeInfo = runtimeData.get(runtimeIdentifier);
    
    if (!runtimeInfo) {
      return {
        blockFunctionCreate: 'Deprecated',
        blockFunctionUpdate: 'Deprecated',
        isDeprecated: false,
        status: 'Unknown Runtime'
      };
    }

    const now = new Date();
    const createBlocked = runtimeInfo.blockFunctionCreate && now >= runtimeInfo.blockFunctionCreate;
    const updateBlocked = runtimeInfo.blockFunctionUpdate && now >= runtimeInfo.blockFunctionUpdate;
    const isDeprecated = runtimeInfo.deprecationDate && now >= runtimeInfo.deprecationDate;

    return {
      blockFunctionCreate: createBlocked ? 'true' : runtimeInfo.rawBlockFunctionCreate || 'false',
      blockFunctionUpdate: updateBlocked ? 'true' : runtimeInfo.rawBlockFunctionUpdate || 'false',
      isDeprecated,
      status: createBlocked && updateBlocked ? 'Fully Blocked' : 
               createBlocked ? 'Create Blocked' : 
               updateBlocked ? 'Update Blocked' : 
               isDeprecated ? 'Deprecated' : 'Active'
    };
  }

  getCachedData(ignoreTTL = false) {
    try {
      const cached = localStorage.getItem(this.cacheKey);
      if (!cached) return null;

      const { data, timestamp } = JSON.parse(cached);
      
      if (!ignoreTTL && Date.now() - timestamp > this.cacheExpiry) {
        localStorage.removeItem(this.cacheKey);
        return null;
      }

      return new Map(Object.entries(data));
    } catch (error) {
      return null;
    }
  }

  setCachedData(data) {
    try {
      const cacheData = {
        data: Object.fromEntries(data),
        timestamp: Date.now()
      };
      localStorage.setItem(this.cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Cache save failed:', error);
    }
  }

  clearCache() {
    try {
      localStorage.removeItem(this.cacheKey);
    } catch (error) {
      console.warn('Cache clear failed:', error);
    }
  }

  getHardcodedFallbackData() {
    const fallbackData = new Map();
    
    // Critical runtimes with conservative dates
    const runtimes = [
      ['nodejs18.x', { name: 'Node.js 18', rawDeprecationDate: 'Sep 1, 2025', rawBlockFunctionCreate: 'Oct 1, 2025', rawBlockFunctionUpdate: 'Nov 1, 2025' }],
      ['python3.9', { name: 'Python 3.9', rawDeprecationDate: 'Nov 3, 2025', rawBlockFunctionCreate: 'Dec 8, 2025', rawBlockFunctionUpdate: 'Jan 8, 2026' }],
      ['java11', { name: 'Java 11', rawDeprecationDate: 'Jun 30, 2026', rawBlockFunctionCreate: 'Jul 31, 2026', rawBlockFunctionUpdate: 'Aug 31, 2026' }],
      ['python3.8', { name: 'Python 3.8', rawDeprecationDate: 'Oct 14, 2024', rawBlockFunctionCreate: 'true', rawBlockFunctionUpdate: 'true' }],
      ['nodejs16.x', { name: 'Node.js 16', rawDeprecationDate: 'Jun 12, 2024', rawBlockFunctionCreate: 'true', rawBlockFunctionUpdate: 'true' }]
    ];
    
    runtimes.forEach(([id, info]) => {
      fallbackData.set(id, {
        identifier: id,
        ...info,
        deprecationDate: info.rawDeprecationDate !== 'true' ? new Date(info.rawDeprecationDate) : new Date('2024-01-01'),
        blockFunctionCreate: info.rawBlockFunctionCreate !== 'true' ? new Date(info.rawBlockFunctionCreate) : new Date('2024-01-01'),
        blockFunctionUpdate: info.rawBlockFunctionUpdate !== 'true' ? new Date(info.rawBlockFunctionUpdate) : new Date('2024-01-01'),
        isFallback: true
      });
    });
    
    console.warn(`⚠️ Using fallback data for ${fallbackData.size} runtimes`);
    return fallbackData;
  }
}

// Export the service
export const runtimeDeprecationService = new QuickCorsRuntimeService();

// Test function for browser console
window.testRuntimeService = async () => {
  console.log('🧪 Testing runtime service...');
  try {
    const data = await runtimeDeprecationService.getRuntimeDeprecationData();
    console.log(`✅ Success! Loaded ${data.size} runtimes`);
    
    // Test a specific runtime
    const status = await runtimeDeprecationService.getRuntimeStatus('nodejs18.x');
    console.log('📋 Node.js 18 status:', status);
    
    return true;
  } catch (error) {
    console.error('❌ Test failed:', error);
    return false;
  }
};

console.log('🚀 Quick CORS Runtime Service loaded. Test with: window.testRuntimeService()');
